import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { StickyNote, Plus, Upload, Loader2, ChevronLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [course, setCourse] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/notes"); setNotes(data); } catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!content.trim()) { toast.error("Write something first"); return; }
    setBusy(true);
    try {
      await api.post("/notes", { title, content, course });
      toast.success("Note saved — extracting concepts…");
      setTitle(""); setContent(""); setCourse(""); setCreating(false);
      load();
    } catch { toast.error("Failed to save note"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/notes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${file.name}`);
      load();
    } catch { toast.error("Failed to import file"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    await api.delete(`/notes/${id}`);
    setActive(null);
    load();
  };

  if (active) {
    return (
      <div className="h-screen overflow-y-auto p-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => setActive(null)} data-testid="back-to-notes" className="flex items-center gap-1.5 text-sm text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] mb-6">
            <ChevronLeft size={14} /> Back to Notes
          </button>
          <div className="flex items-start justify-between mb-2">
            <h1 className="font-display text-2xl">{active.title}</h1>
            <button onClick={() => remove(active.note_id)} data-testid="delete-note-button" className="text-[var(--aria-danger)] hover:opacity-70"><Trash2 size={16} /></button>
          </div>
          <div className="flex items-center gap-3 text-xs text-[var(--aria-text-muted)] mb-6">
            {active.course && <span>{active.course}</span>}
            <span>{new Date(active.created_at).toLocaleDateString()}</span>
          </div>
          {active.concepts?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {active.concepts.map(c => <span key={c} className="aria-pill aria-pill-primary text-xs">{c}</span>)}
            </div>
          )}
          <div className="aria-card p-5 whitespace-pre-wrap text-sm leading-relaxed">{active.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl mb-1">Notes</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Type, paste, or upload — ARIA extracts concepts and links them into your knowledge graph.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()} data-testid="notes-upload-button"
              className="btn px-4 py-2 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] text-sm flex items-center gap-2">
              <Upload size={14} /> Upload
            </button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.txt,.md" onChange={e => upload(e.target.files[0])} />
            <button onClick={() => setCreating(!creating)} data-testid="new-note-button"
              className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2">
              <Plus size={14} /> New note
            </button>
          </div>
        </div>

        {creating && (
          <div className="aria-card p-5 mb-6 space-y-3" data-testid="note-editor">
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
              data-testid="note-title-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm font-medium" />
            <input value={course} onChange={e => setCourse(e.target.value)} placeholder="Course (optional)"
              data-testid="note-course-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={8} placeholder="Write or paste your notes…"
              data-testid="note-content-input"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)]">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="save-note-button"
                className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50 flex items-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {notes.map(n => (
            <div key={n.note_id} onClick={() => setActive(n)} data-testid={`note-card-${n.note_id}`}
              className="aria-card aria-magnetic p-5 cursor-pointer">
              <StickyNote size={18} className="text-[var(--aria-primary)] mb-3" />
              <h3 className="font-display text-lg leading-tight mb-1 line-clamp-2">{n.title}</h3>
              <p className="text-xs text-[var(--aria-text-muted)] mb-3 line-clamp-2">{n.content}</p>
              {n.concepts?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {n.concepts.slice(0, 3).map(c => <span key={c} className="aria-pill text-[10px]">{c}</span>)}
                </div>
              )}
              {n.is_demo && <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--aria-text-faint)] font-mono">Demo data</div>}
            </div>
          ))}
          {notes.length === 0 && !creating && (
            <div className="col-span-full text-center py-16 text-[var(--aria-text-muted)]">
              No notes yet — click "New note" or upload a file to get started.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
